<template>
  <div class="container pl-20 pr-20 pt-10 ">
    <div class="row pl-3 pr-3">
      <b-button
        v-if="canSubscribeThirdParties"
        class="mr-3 btn-icon fb-bg-primary"
        :class="{ 'cursor-not-allowed': userPermissions.totalControl === 0 }"
        v-tooltip="'Suscribir a un usuario'"
        variant="primary"
        @click="newSubscription"
        :disabled="userPermissions.totalControl === 0"
        data-cy="subscribe-a-user"
      >
        <feather-icon icon="UsersIcon" />
      </b-button>
      <b-button
        class="mr-3 btn-icon fb-bg-primary"
        icon="UserIcon"
        v-tooltip="'Suscribirme'"
        @click="selfSubscription"
        variant="primary"
        v-if="!disabledSelfSubscription"
        data-cy="subscribe"
      >
        <feather-icon icon="UserIcon" />
      </b-button>
    </div>
    <div class="row justify-content-center mt-8">
      <app-timeline class="mb-24">
        <app-timeline-item
          variant="secondary"
          v-for="(subscriber, index) in subscribersList"
          :key="subscriber.suscripcionId"
        >
          <b-media v-show="!loading">
            <template #aside>
              <b-avatar :src="getAvatar()" />
            </template>
            <div class="d-flex flex-sm-row flex-column
              flex-wrap justify-content-between mb-1 mb-sm-0"
            >
              <h6>{{ subscriber.nombre }}</h6>
              <feather-icon
                icon="Trash2Icon"
                size="16"
                class="text-danger cursor-pointer"
                v-tooltip="'Eliminar suscripción'"
                data-cy="eliminate-subscription"
                @click="removeSubscription(index)"
                v-if="userPermissions.totalControl === 1
                  || currentUser.id === subscriber.usuarioSuscrito"
              />
            </div>
            <span v-if="subscriber.suscripcionHasta">
              Suscrito desde
              {{
                $moment(subscriber.suscripcionDesde, "YYYY-MM-DD")
                .format('DD-MM-YYYY')
              }} hasta {{
                $moment(subscriber.suscripcionHasta, "YYYY-MM-DD")
                .format('DD-MM-YYYY')
              }}
            </span>
            <span v-else>
              Suscrito desde {{ $moment(subscriber.suscripcionDesde).format('DD-MM-YYYY') }}
            </span>
          </b-media>
          <b-media v-show="loading">
            <template #aside>
              <b-skeleton type="avatar" animation="fade"/>
            </template>
            <div class="d-flex flex-sm-row flex-column
              flex-wrap justify-content-between mb-1 mb-sm-0"
            >
              <b-skeleton class="w-32" />
              <b-skeleton class="w-5" />
            </div>
            <p>
              <b-skeleton class="w-64" />
            </p>
          </b-media>
        </app-timeline-item>
      </app-timeline>
    </div>
    <div  v-if="!subscribersList.length">
      <h5 class="mt-3 text-center">No hay suscripciones para este elemento</h5>
    </div>
    <!-- SUBSCRIPTION FORM -->
    <SubscriptionForm :isSelfSubscription="isSelfSubscription"/>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { BAvatar } from 'bootstrap-vue';

import {
  ioEDFileSubscriptionsDelete,
  ioEDFoldersSubscriptionsDelete
} from '@/febos/servicios/api/repositories.api';
import SubscriptionForm from '@/febos/global/documentManagement/vistas/modals/SubscriptionForm';
import AppTimeline from '@core/components/app-timeline/AppTimeline';
import AppTimelineItem from '@core/components/app-timeline/AppTimelineItem';

export default {
  name: 'SubscriptionsView',
  data() {
    return {
      userPermissions: {
        read: 0,
        write: 0,
        totalControl: 0
      },
      subscribersList: [],
      loading: false,
      isSelfSubscription: false
    };
  },
  components: {
    SubscriptionForm,
    AppTimeline,
    AppTimelineItem,
    BAvatar
  },
  mounted() {
    this.loadSubscriptions();
    this.loadPermissions();
  },
  computed: {
    ...mapGetters('Usuario', [
      'currentUser',
      'hasPermission'
    ]),
    ...mapGetters('DocManagement', [
      'getSelectedElement',
      'getSubscriptionsStack',
      'getPermissionsStack'
    ]),
    currentAvatar() {
      return this.currentUser.avatar || require('../../../../assets/images/no-avatar.svg');
    },
    disabledSelfSubscription() {
      if (this.getSubscriptionsStack[this.getSelectedElement.id]) {
        const iamSubscribed = this.getSubscriptionsStack[this.getSelectedElement.id].some(
          (subscription) => subscription.usuarioSuscrito === this.currentUser.id
        );
        if (iamSubscribed) {
          return true;
        }
      }
      return false;
    },
    canSubscribeThirdParties() {
      if (this.hasPermission('FEB99')
        || this.getSelectedElement.ownerId === this.currentUser.id
        || this.userPermissions.totalControl === 1) {
        return true;
      }
      return false;
    }
  },
  methods: {
    ...mapActions('Modals', [
      'showModals'
    ]),
    ...mapActions('DocManagement', [
      'loadFileSubscriptions'
    ]),
    async loadSubscriptions() {
      this.loading = true;
      await this.loadFileSubscriptions({
        repositoryId: this.getSelectedElement.repositoryId,
        fileId: this.getSelectedElement.id
      });
      this.subscribersList = this.getSubscriptionsStack[this.getSelectedElement.id];
      this.loading = false;
    },
    newSubscription() {
      this.isSelfSubscription = false;
      this.showModals('subscriptionFormED');
    },
    selfSubscription() {
      this.isSelfSubscription = true;
      this.showModals('subscriptionFormED');
    },
    getAvatar(path) {
      return path || require('../../../../assets/images/no-avatar.svg');
    },
    async removeSubscription(index) {
      if (this.getSelectedElement.type === 'file') {
        await ioEDFileSubscriptionsDelete({
          subscriptionId: this.subscribersList[index].suscripcionId,
          repositoryId: this.getSelectedElement.repositoryId,
          fileId: this.getSelectedElement.id
        });
      }
      if (this.getSelectedElement.type === 'folder') {
        await ioEDFoldersSubscriptionsDelete({
          subscriptionId: this.subscribersList[index].suscripcionId,
          repositoryId: this.getSelectedElement.repositoryId,
          folderId: this.getSelectedElement.id
        });
      }
      this.subscribersList.splice(index, 1);
    },
    loadPermissions() {
      const selected = this.getSelectedElement;
      this.userPermissions.read = selected.loggedUserPermissions.lectura;
      this.userPermissions.write = selected.loggedUserPermissions.escritura;
      this.userPermissions.totalControl = selected.loggedUserPermissions.controlTotal;
    }
  },
  watch: {
    getSelectedElement() {
      this.loadSubscriptions();
      this.loadPermissions();
    },
    getSubscriptionsStack() {
      this.subscribersList = this.getSubscriptionsStack[this.getSelectedElement.id];
    }
  }
};
</script>

<style lang="scss" scoped>
.fb-bg-primary{
  background-color: rgb(var(--vs-primary)) !important;
}
</style>
